import React, { useEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { t } from "../i18n/translations";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

function cloneOverrides(value) {
    if (!value || typeof value !== "object") {
        return {};
    }
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        console.warn("Failed to clone overrides", error);
        return {};
    }
}

function isIndexSegment(segment) {
    return /^\d+$/.test(segment);
}

function ensureContainer(parent, key, makeArray) {
    if (makeArray) {
        if (!Array.isArray(parent[key])) {
            parent[key] = [];
        }
        return parent[key];
    }
    const current = parent[key];
    if (!current || typeof current !== "object" || Array.isArray(current)) {
        parent[key] = {};
    }
    return parent[key];
}

function setNestedValue(root, parts, value) {
    if (!parts.length) return;
    let cursor = root;
    for (let index = 0; index < parts.length; index += 1) {
        const segment = parts[index];
        const nextIsIndex = index + 1 < parts.length ? isIndexSegment(parts[index + 1]) : false;
        const isIndex = isIndexSegment(segment);
        const isLast = index === parts.length - 1;

        if (isLast) {
            if (isIndex) {
                if (Array.isArray(cursor)) {
                    cursor[Number(segment)] = value;
                } else {
                    const key = segment;
                    const container = ensureContainer(cursor, key, true);
                    container[Number(segment)] = value;
                }
            } else if (Array.isArray(cursor)) {
                cursor[Number(segment)] = value;
            } else {
                cursor[segment] = value;
            }
            return;
        }

        if (Array.isArray(cursor)) {
            const numericIndex = Number(segment);
            if (!cursor[numericIndex] || typeof cursor[numericIndex] !== "object") {
                cursor[numericIndex] = nextIsIndex ? [] : {};
            }
            cursor = cursor[numericIndex];
            continue;
        }

        if (isIndex) {
            const key = segment;
            cursor = ensureContainer(cursor, key, true);
        } else {
            cursor = ensureContainer(cursor, segment, nextIsIndex);
        }
    }
}

export default function EditableText({ translationKey, children, multiline = false }) {
    const { currentUser, saveSiteContent, siteContent } = useAuth();
    const { language } = useLanguage();
    const isAdmin = currentUser?.role === "admin";
    const value = t(translationKey, language);
    const safeValue = typeof value === "string" ? value : "";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(safeValue);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(0);

    useEffect(() => {
        if (!editing) {
            setDraft(safeValue);
        }
    }, [safeValue, editing]);

    useEffect(() => {
        if (!savedAt) return undefined;
        const timer = setTimeout(() => {
            setSavedAt(0);
        }, 2500);
        return () => clearTimeout(timer);
    }, [savedAt]);

    const label = useMemo(() => {
        if (!isAdmin) return "";
        return t("inlineEdit.edit", language);
    }, [isAdmin, language]);

    const handleEdit = () => {
        setEditing(true);
        setError("");
        setSavedAt(0);
    };

    const handleCancel = () => {
        setEditing(false);
        setDraft(safeValue);
        setError("");
        setPending(false);
        setSavedAt(0);
    };

    const handleSave = async () => {
        setPending(true);
        setError("");
        const overrides = cloneOverrides(siteContent?.[language]);
        setNestedValue(overrides, translationKey.split("."), draft);
        const result = await saveSiteContent(language, overrides);
        setPending(false);
        if (!result?.ok) {
            setError(result?.error || "Unable to save changes.");
            return;
        }
        setEditing(false);
        setSavedAt(Date.now());
    };

    if (!children) {
        return null;
    }

    return (
        <div className={`relative ${isAdmin ? "group" : ""}`}>
            {children(safeValue)}
            {isAdmin && !editing ? (
                <button
                    type="button"
                    onClick={handleEdit}
                    className="absolute -top-3 -right-3 hidden items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 group-hover:flex"
                    aria-label={label}
                >
                    <Pencil className="h-3 w-3" />
                    {t("inlineEdit.edit", language)}
                </button>
            ) : null}
            {isAdmin && editing ? (
                <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white/95 p-3 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900/95">
                    {multiline ? (
                        <Textarea
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            rows={4}
                        />
                    ) : (
                        <Input value={draft} onChange={(event) => setDraft(event.target.value)} />
                    )}
                    {error ? <p className="text-xs text-red-600">{error}</p> : null}
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" className="rounded-xl" onClick={handleSave} disabled={pending}>
                            {pending ? t("inlineEdit.saving", language) : t("inlineEdit.save", language)}
                        </Button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={pending}
                            className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                            {t("inlineEdit.cancel", language)}
                        </button>
                    </div>
                </div>
            ) : null}
            {isAdmin && !editing && savedAt ? (
                <p className="mt-1 text-xs text-emerald-600">{t("inlineEdit.saved", language)}</p>
            ) : null}
        </div>
    );
}
